import * as React from "react";
import { DataGridPremium, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useDispatch, useSelector } from "react-redux";
import CclDataGridToolbar from "../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclCommonLink from "../../components/common/cclCommonLink";
import { selectInventory, setShowZeroQuantityOrders } from "../../app/slices/inventorySlice";
import { useGetGroupOrdersQuery } from "../../services/cclTokenedAssessmentApi";

interface OrdersInventoryDataGridProps {
    groupKey: number;
    groupName?: string;
}
const buildColumns = (groupKey: number, groupName?: string): GridColDef[] => [
    {
        field: "displayOrderId",
        headerName: "Order #",
        headerAlign: "left",
        align: "left",
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
            const query = groupName
                ? `?groupId=${groupKey}&groupName=${encodeURIComponent(groupName)}`
                : "";
            return (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/inventory/${params.value}${query}`}
                />
            );
        },
    },
    {
        field: "instrumentName",
        headerName: "Assessment",
        headerAlign: "left",
        align: "left",
        flex: 2,
    },
    {
        field: "orderQty",
        headerName: "Quantity",
        type: "number",
        headerAlign: "left",
        align: "left",
        flex: 1,
    },
    {
        field: "availableQty",
        headerName: "Available",
        type: "number",
        headerAlign: "left",
        align: "left",
        flex: 1,
    },
    {
        field: "placedBy",
        headerName: "Placed By",
        headerAlign: "left",
        align: "left",
        flex: 2,
    },
    {
        field: "orderDate",
        headerName: "Placed",
        headerAlign: "left",
        align: "left",
        flex: 1,
        valueFormatter: (params: { value: string }) =>
            params.value ? new Date(params.value).toLocaleDateString() : "",
    },
];

const OrdersInventoryDataGrid: React.FC<OrdersInventoryDataGridProps> = (props) => {
    const dispatch = useDispatch();
    const { showZeroQuantityOrders } = useSelector(selectInventory);

    const { data, isLoading } = useGetGroupOrdersQuery(props.groupKey, {
        skip: !props.groupKey || !Number.isFinite(props.groupKey),
    });
    const rows = React.useMemo(() => {
        const filtered = showZeroQuantityOrders
            ? (data ?? [])
            : (data ?? []).filter((r) => r.availableQty > 0);
        return filtered.map((row, index) => ({ ...row, id: `${row.epicorOrderId}_${index}` }));
    }, [data, showZeroQuantityOrders]);
    const columns = React.useMemo(
        () => buildColumns(props.groupKey, props.groupName),
        [props.groupKey, props.groupName],
    );

    return (
        <DataGridPremium
            rows={rows}
            columns={columns}
            loading={isLoading}
            autoHeight
            disableSelectionOnClick
            sx={{ "& .MuiFormControlLabel-root": { ml: 0.5, mr: 0, mt: 2 } }}
            components={{ Toolbar: CclDataGridToolbar }}
            componentsProps={{
                toolbar: {
                    showSaveState: false,
                    hideSettingsButtons: true,
                    switchToggled: (_btnId: string, checked: boolean) =>
                        dispatch(setShowZeroQuantityOrders(checked)),
                    miscButtons: [
                        {
                            id: "show-zero-quantity-orders",
                            type: "switch",
                            caption: "Show 0 Available Orders",
                            restricted: false,
                            returnSelected: false,
                            checkedState: showZeroQuantityOrders,
                            disable: false,
                            onClick: function (rows?: any[]) {
                                return;
                            },
                        },
                    ],
                },
            }}
        />
    );
};

export default OrdersInventoryDataGrid;
